import {Utils} from "../common/utils";
import useSWR from "swr";
import type {LabelGroup, Label} from "Stores/labelStore";

import fetcher from "@/common/fetcher";
import {
    deleteApiV1SchoolsBySchoolIdLabelsByLabelId,
    deleteApiV1SchoolsBySchoolIdLabelsGroupsByGroupId,
    getApiV1SchoolsBySchoolIdLabels,
    getApiV1SchoolsBySchoolIdLabelsByLabelId,
    getApiV1SchoolsBySchoolIdLabelsGroups,
    postApiV1SchoolsBySchoolIdLabels,
    postApiV1SchoolsBySchoolIdLabelsGroups,
    putApiV1SchoolsBySchoolIdLabelsByLabelId,
    putApiV1SchoolsBySchoolIdLabelsGroupsByGroupId
} from "Services/platform/client";
import { isPlatformApiEnabled } from "Services/platform/module-flags";
import {
    labelDtoToLabel,
    labelGroupDtoToLabelGroup,
    labelGroupId,
    labelGroupToCreateRequest,
    labelGroupToUpdateRequest,
    labelToCreateRequest,
    labelToUpdateRequest
} from "Services/platform/label-mappers";
import type { LabelGroupWriteFields, LabelWriteFields } from "Services/platform/label-mappers";
import { EAIClient } from "Services/EAIClient";

const clientConfigs = Utils.getEAIClientConfigs();
const eaiClient = new EAIClient();

export interface LabelsResponse {
    data: Label[]
}

export interface LabelGroupResponse {
    data: LabelGroup[]
}

/**
 * Label reads and writes go to platform-api or the legacy API depending on the
 * `labels` module flag. The generated SDK resolves with an `error` payload
 * instead of throwing, so every platform-api call checks and throws.
 */

/** The SDK resolves with the error payload; rethrow it as an Error so stacks and Sentry work. */
function toError(error: unknown, fallback: string): Error {
    if (error instanceof Error) {
        return error;
    }
    const message = (error as {message?: unknown} | null)?.message;
    return new Error(typeof message === "string" && message ? message : fallback);
}

function requireSchool(): string {
    const schoolId = clientConfigs.school;

    if (!schoolId) {
        throw new Error("No school selected; cannot load labels.");
    }

    return schoolId;
}

function byOrdering(groups: LabelGroup[]): LabelGroup[] {
    return [...groups].sort((first, second) => first.ordering - second.ordering);
}

export async function fetchLabels(): Promise<Label[]> {
    if (!isPlatformApiEnabled("labels")) {
        const response = await eaiClient.fetchLabels();
        return response.data.data;
    }

    const {data, error} = await getApiV1SchoolsBySchoolIdLabels({path: {schoolId: requireSchool()}});

    if (error) {
        throw toError(error, "Failed to load labels.");
    }

    return (data?.data ?? []).map(labelDtoToLabel);
}

export async function fetchLabelGroups(): Promise<LabelGroup[]> {
    if (!isPlatformApiEnabled("labels")) {
        const response = await eaiClient.fetchLabels(true);
        return response.data.data;
    }

    const {data, error} = await getApiV1SchoolsBySchoolIdLabelsGroups({
        path: {schoolId: requireSchool()},
        query: {includeLabels: true}
    });

    if (error) {
        throw toError(error, "Failed to load label groups.");
    }

    // Drag-to-reorder on the Label Groups page renders in list order.
    return byOrdering((data?.data ?? []).map(labelGroupDtoToLabelGroup));
}

export async function fetchLabel(labelId: number): Promise<Label | undefined> {
    if (!isPlatformApiEnabled("labels")) {
        const response = await eaiClient.fetchLabel(String(labelId));
        return response.data.data;
    }

    const {data, error} = await getApiV1SchoolsBySchoolIdLabelsByLabelId({
        path: {schoolId: requireSchool(), labelId}
    });

    if (error) {
        throw toError(error, "Failed to load label.");
    }

    return data?.data ? labelDtoToLabel(data.data) : undefined;
}

export async function createLabel(fields: LabelWriteFields): Promise<void> {
    if (!isPlatformApiEnabled("labels")) {
        await eaiClient.createLabel({name: fields.name, label_group: labelGroupId(fields.label_group)});
        return;
    }

    const {error} = await postApiV1SchoolsBySchoolIdLabels({
        path: {schoolId: requireSchool()},
        body: labelToCreateRequest(fields)
    });

    if (error) {
        throw toError(error, "Failed to create label.");
    }
}

/**
 * On platform-api `changes` are merged over `label` and every field is sent, so the update is
 * safe whether the PUT patches or replaces. The legacy API gets `changes` as before.
 */
export async function updateLabel(label: Label, changes: LabelWriteFields): Promise<void> {
    if (!isPlatformApiEnabled("labels")) {
        await eaiClient.updateLabel(label.id, {...changes, label_group: labelGroupId(changes.label_group)});
        return;
    }

    const {error} = await putApiV1SchoolsBySchoolIdLabelsByLabelId({
        path: {schoolId: requireSchool(), labelId: label.id},
        body: labelToUpdateRequest({
            name: label.name,
            label_group: label.label_group,
            is_archived: label.is_archived,
            ...changes
        })
    });

    if (error) {
        throw toError(error, "Failed to update label.");
    }
}

export async function deleteLabel(labelId: number): Promise<void> {
    if (!isPlatformApiEnabled("labels")) {
        await eaiClient.deleteLabel(labelId);
        return;
    }

    const {data, error} = await deleteApiV1SchoolsBySchoolIdLabelsByLabelId({
        path: {schoolId: requireSchool(), labelId}
    });

    if (error) {
        throw toError(error, "Failed to delete label.");
    }
    if (data?.data === false) {
        throw new Error("The label was not deleted.");
    }
}

export async function createLabelGroup(fields: LabelGroupWriteFields): Promise<void> {
    if (!isPlatformApiEnabled("labels")) {
        await eaiClient.createLabelGroup({name: fields.name ?? "", order: fields.order ?? fields.ordering});
        return;
    }

    const {error} = await postApiV1SchoolsBySchoolIdLabelsGroups({
        path: {schoolId: requireSchool()},
        body: labelGroupToCreateRequest(fields)
    });

    if (error) {
        throw toError(error, "Failed to create label group.");
    }
}

/** Same merge as {@link updateLabel}: platform-api always gets the group's name and ordering. */
export async function updateLabelGroup(group: LabelGroup, changes: LabelGroupWriteFields): Promise<void> {
    if (!isPlatformApiEnabled("labels")) {
        await eaiClient.updateLabelGroup(group.id, changes);
        return;
    }

    const {error} = await putApiV1SchoolsBySchoolIdLabelsGroupsByGroupId({
        path: {schoolId: requireSchool(), groupId: group.id},
        body: labelGroupToUpdateRequest({name: group.name, ordering: group.ordering, ...changes})
    });

    if (error) {
        throw toError(error, "Failed to update label group.");
    }
}

export async function deleteLabelGroup(groupId: number): Promise<void> {
    if (!isPlatformApiEnabled("labels")) {
        await eaiClient.deleteLabelGroup(groupId);
        return;
    }

    const {data, error} = await deleteApiV1SchoolsBySchoolIdLabelsGroupsByGroupId({
        path: {schoolId: requireSchool(), groupId}
    });

    if (error) {
        throw toError(error, "Failed to delete label group.");
    }
    if (data?.data === false) {
        throw new Error("The label group was not deleted.");
    }
}

/** The flag is part of each key so flipping it refetches rather than serving the other API's cache. */
type LabelsKey = readonly ["labels", string, boolean];
type LabelGroupsKey = readonly ["labelGroups", string, boolean];

export function useLabels() {
    const usePlatformApi = isPlatformApiEnabled("labels");
    const schoolId = clientConfigs.school;
    const key: LabelsKey | null = schoolId ? ["labels", schoolId, usePlatformApi] : null;

    const {data, mutate} = useSWR<Label[], Error>(key, ([, school, viaPlatformApi]: LabelsKey) =>
        viaPlatformApi
            ? fetchLabels()
            : fetcher(`${clientConfigs.endpoint}/schools/${school}/labels?grouped=false`, {})
                .then((response: LabelsResponse) => response.data ?? [])
    );

    return {data: data ? {data} as LabelsResponse : undefined, mutate};
}

export function useLabelGroups() {
    const usePlatformApi = isPlatformApiEnabled("labels");
    const schoolId = clientConfigs.school;
    const key: LabelGroupsKey | null = schoolId ? ["labelGroups", schoolId, usePlatformApi] : null;

    const {data, mutate} = useSWR<LabelGroup[], Error>(key, ([, school, viaPlatformApi]: LabelGroupsKey) =>
        viaPlatformApi
            ? fetchLabelGroups()
            : fetcher(`${clientConfigs.endpoint}/schools/${school}/labels?grouped=true`, {})
                .then((response: LabelGroupResponse) => response.data ?? [])
    );

    return {data: data ? {data} as LabelGroupResponse : undefined, mutate};
}
